import { useState, useCallback } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { PipelineBoard } from '@/components/crm/PipelineBoard'
import { LeadTable } from '@/components/crm/LeadTable'
import { LeadDetail } from '@/components/crm/LeadDetail'
import { CRMStats } from '@/components/crm/CRMStats'
import { useLeads, useRecentOutreach } from '@/hooks/useCRM'
import { type CrmLead } from '@/lib/supabase'
import { LayoutGrid, List, BarChart3, Mail } from 'lucide-react'
import { cn } from '@/lib/utils'

type CrmTab = 'pipeline' | 'leads' | 'outreach' | 'stats'

export function CRM() {
  const [tab, setTab] = useState<CrmTab>('pipeline')
  const [selectedLead, setSelectedLead] = useState<CrmLead | null>(null)
  const [detailOpen, setDetailOpen] = useState(false)
  const { refetch: refetchLeads } = useLeads({})
  const { outreach, loading: outreachLoading } = useRecentOutreach(50)

  const handleSelectLead = useCallback((lead: CrmLead) => {
    setSelectedLead(lead)
    setDetailOpen(true)
  }, [])

  const handleDetailUpdated = useCallback(() => {
    refetchLeads()
  }, [refetchLeads])

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })

  const tabs: { id: CrmTab; label: string; icon: typeof LayoutGrid }[] = [
    { id: 'pipeline', label: 'Pipeline', icon: LayoutGrid },
    { id: 'leads', label: 'Leads', icon: List },
    { id: 'outreach', label: 'Outreach', icon: Mail },
    { id: 'stats', label: 'Stats', icon: BarChart3 },
  ]

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold">CRM Pipeline</h1>
          <p className="text-muted-foreground">Sales leads and outreach</p>
        </div>
      </div>

      <div className="flex gap-2 border-b">
        {tabs.map(({ id, label, icon: Icon }) => (
          <Button
            key={id}
            variant={tab === id ? 'default' : 'ghost'}
            size="sm"
            className={cn('gap-2', tab === id && 'border-b-2 border-primary')}
            onClick={() => setTab(id)}
          >
            <Icon className="h-4 w-4" />
            {label}
          </Button>
        ))}
      </div>

      {tab === 'pipeline' && (
        <PipelineBoard onSelectLead={handleSelectLead} />
      )}

      {tab === 'leads' && (
        <LeadTable onSelectLead={handleSelectLead} />
      )}

      {tab === 'outreach' && (
        <Card>
          <CardHeader>
            <CardTitle>Recent outreach</CardTitle>
            <p className="text-sm text-muted-foreground">Last 50 touchpoints</p>
          </CardHeader>
          <CardContent>
            {outreachLoading ? (
              <div className="flex h-32 items-center justify-center">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
              </div>
            ) : (
              <div className="space-y-2">
                {outreach.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No outreach yet.</p>
                ) : (
                  outreach.map((o) => (
                    <div
                      key={o.id}
                      className="flex items-center justify-between rounded-lg border p-3 text-sm"
                    >
                      <div>
                        <span className="font-medium capitalize">{o.type}</span>
                        <span className="text-muted-foreground"> · </span>
                        <span className="text-muted-foreground">{o.sent_by}</span>
                        {o.subject && (
                          <p className="mt-0.5 text-muted-foreground">{o.subject}</p>
                        )}
                        <p className="text-xs text-muted-foreground">
                          {o.crm_leads?.full_name || o.crm_leads?.email || '—'}
                        </p>
                      </div>
                      <span className="text-xs text-muted-foreground">{formatDate(o.created_at)}</span>
                    </div>
                  ))
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {tab === 'stats' && <CRMStats />}

      <LeadDetail
        lead={selectedLead}
        open={detailOpen}
        onOpenChange={setDetailOpen}
        onUpdated={handleDetailUpdated}
      />
    </div>
  )
}
